export type CampaignType = "email" | "sms";

export interface CampaignFilters {
  includeZipCodes: string[] | null;
  excludeZipCodes: string[] | null;
  includeCities: string[] | null;
  excludeCities: string[] | null;
  includeStates: string[] | null;
  excludeStates: string[] | null;
  includeTags: number[] | null;
  excludeTags: number[] | null;
  includePropertyTags: number[] | null;
  excludePropertyTags: number[] | null;
  activeStatus: "active" | "inactive" | "all" | null;
  servicePlanIds: number[] | null;
  excludeServicePlanIds: number[] | null;
  servicePlanExpiringDays: number | null;
  lastApptBefore: string | null;
  lastApptAfter: string | null;
  servicesUsed: number[] | null;
  outstandingBalance: boolean | null;
  unrespondedEstimates: boolean | null;
  unrespondedEstimatesDays: number | null;
  includeCustomerIds: number[] | null;
  excludeCustomerIds: number[] | null;
  manualEmails: string[] | null;
  manualPhones: string[] | null;
  appointmentTypeIds: number[] | null;
  excludeAppointmentTypeIds: number[] | null;
  requestTypeIds: number[] | null;
  excludeRequestTypeIds: number[] | null;
}

export interface SelectedCustomer {
  id: number;
  name: string;
  email: string;
  companyName?: string;
}

export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "failed"
  | "cancelled";

export interface ICampaign {
  id: string;
  businessId: string;
  campaignType: CampaignType;
  templateId: string | null;
  name: string;
  subject: string | null;
  body: string | null;
  designJson: Record<string, unknown> | null;
  messageText: string | null;
  status: CampaignStatus;
  sendDate: string | null;
  sentAt: string | null;
  sentBy: string | null;
  sentByName?: string | null;
  /** Pending schedule (when status is "scheduled") — populated by getCampaign. */
  schedule?: { scheduledAt: string; timezone: string } | null;
  totalRecipients: number;
  totalSent: number;
  totalFailed: number;
  filters: CampaignFilters;
  createdAt: string;
  updatedAt: string;
}

export interface LocationCombo {
  state: string;
  city: string;
  zipcode: string;
}

export interface FilterOptions {
  servicePlans: { id: number; name: string }[];
  services: { id: number; name: string }[];
  customerTags: { id: number; name: string; color?: string | null }[];
  propertyTags: { id: number; name: string; color?: string | null }[];
  zipCodes: string[];
  cities: string[];
  states: string[];
  locationCombos: LocationCombo[];
  appointmentTypes: { id: number; name: string }[];
  requestTypes: { id: number; name: string }[];
}

export interface Recipient {
  bcustomerId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
  companyName: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  /** Set (opt-out timestamp) when the customer unsubscribed from this campaign's channel — preview only. */
  optedOutAt?: string | null;
}

export interface RecipientCountResponse {
  count: number;
}

export interface CampaignReportEntry {
  recipientAddress: string;
  channel: CampaignType;
  status: string;
  sentAt: string | null;
  errorMessage: string | null;
  openedAt: string | null;
  openCount: number;
  firstClickedAt: string | null;
  clickCount: number;
}

export interface TrackingSummary {
  uniqueOpens: number;
  totalOpens: number;
  uniqueClicks: number;
  totalClicks: number;
}

/**
 * Delivery counts for a campaign, counted from the send log — the same rows the
 * delivery table lists.
 *
 * The campaign's own totalSent/totalFailed are written when the send finishes
 * and never revised, so a later delivery result (a Twilio failure callback, say)
 * leaves them contradicting the rows. These are read fresh with the report.
 */
export interface CampaignSendSummary {
  total: number;
  sent: number;
  failed: number;
  queued: number;
}

export interface CampaignReport {
  campaign: ICampaign;
  entries: CampaignReportEntry[];
  tracking: TrackingSummary;
  summary: CampaignSendSummary;
}

export const DEFAULT_FILTERS: CampaignFilters = {
  includeZipCodes: null,
  excludeZipCodes: null,
  includeCities: null,
  excludeCities: null,
  includeStates: null,
  excludeStates: null,
  includeTags: null,
  excludeTags: null,
  includePropertyTags: null,
  excludePropertyTags: null,
  activeStatus: null,
  servicePlanIds: null,
  excludeServicePlanIds: null,
  servicePlanExpiringDays: null,
  lastApptBefore: null,
  lastApptAfter: null,
  servicesUsed: null,
  outstandingBalance: null,
  unrespondedEstimates: null,
  unrespondedEstimatesDays: null,
  includeCustomerIds: null,
  excludeCustomerIds: null,
  manualEmails: null,
  manualPhones: null,
  appointmentTypeIds: null,
  excludeAppointmentTypeIds: null,
  requestTypeIds: null,
  excludeRequestTypeIds: null,
};
